import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle, ArrowLeft } from "lucide-react";
import RALayout from "../components/ra/RALayout";
import { getPaymentStatus } from "../lib/adventureApi";

const POLL_MS = 2500;
const MAX_POLLS = 12;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await getPaymentStatus(sessionId);
        setInfo(r);
        if (r.payment_status === "paid") { setState("paid"); return; }
        if (r.payment_status === "failed" || r.payment_status === "expired") { setState("error"); return; }
        tries += 1;
        if (tries >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, POLL_MS);
      } catch {
        tries += 1;
        if (tries >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <RALayout>
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-10 h-10 text-terracotta animate-spin mx-auto" />
            <h1 className="font-display font-extrabold text-2xl mt-5">Confirming your payment…</h1>
            <p className="text-ink-secondary mt-2">This only takes a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-paid">
            <div className="w-16 h-16 rounded-full bg-moss-light flex items-center justify-center mx-auto"><Check className="w-8 h-8 text-moss" /></div>
            <h1 className="font-display font-extrabold text-3xl mt-5">Payment confirmed!</h1>
            <p className="text-ink-secondary mt-3">
              Thank you — your {info?.currency ? `A$${info?.amount}` : "deposit"} for <b>{info?.trip_name || "your trip"}</b> is secured.
              Our Australian team will be in touch with the next steps.
            </p>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="payment-pending">
            <Loader2 className="w-10 h-10 text-moss animate-spin mx-auto" />
            <h1 className="font-display font-extrabold text-2xl mt-5">Payment is processing</h1>
            <p className="text-ink-secondary mt-2">We'll email you a confirmation as soon as it clears.</p>
          </div>
        )}
        {state === "error" && (
          <div data-testid="payment-error">
            <XCircle className="w-12 h-12 text-terracotta mx-auto" />
            <h1 className="font-display font-extrabold text-2xl mt-5">We couldn't confirm this payment</h1>
            <p className="text-ink-secondary mt-2">If you were charged, please contact our team and we'll sort it out.</p>
          </div>
        )}
        <Link to="/" data-testid="payment-home-link" className="mt-8 inline-flex items-center gap-2 bg-ink text-white h-11 px-6 rounded-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
    </RALayout>
  );
}

export function PaymentCancel() {
  return (
    <RALayout>
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
        <XCircle className="w-12 h-12 text-ink-muted mx-auto" />
        <h1 className="font-display font-extrabold text-3xl mt-5">Payment cancelled</h1>
        <p className="text-ink-secondary mt-3">No charge was made. You can reserve your trip again whenever you're ready.</p>
        <Link to="/" data-testid="payment-cancel-home-link" className="mt-8 inline-flex items-center gap-2 bg-ink text-white h-11 px-6 rounded-sm font-semibold">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
    </RALayout>
  );
}
